import React, { useState, useEffect } from 'react';
import { knowledgeGraph } from '@/services/knowledgeGraphService';
import PaymentGateway from '@/components/PaymentGateway';
import {
  Lock,
  Unlock,
  ShieldCheck,
  MapPin,
  Star,
  Briefcase,
  Calendar,
  Headphones,
  Ticket,
  Layers,
  Search,
  BarChart2,
} from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import UrgentBookingForm from '@/components/UrgentBookingForm';
import { Button } from '@/components/ui/Button';
import { UrgentBookingRequestDetails, RosterMember } from '@/types';
import { API_BASE_URL } from '@/config';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useAuth } from '@/contexts/AuthContext';

interface RosterProps {
  onNavigate: (view: string) => void;
  onViewProfile: (id: string) => void;
  currentUserId?: string;
}

const Roster: React.FC<RosterProps> = ({ onNavigate, onViewProfile, currentUserId }) => {
  const { notify } = useToast();
  const { isSubscribed, activateSubscription } = useSubscription();
  const { token, setToken } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [showSubscriptionPayment, setShowSubscriptionPayment] = useState(false);
  const [showUrgentBookingPayment, setShowUrgentBookingPayment] = useState(false);
  const [showUrgentBookingForm, setShowUrgentBookingForm] = useState(false);
  const [selectedMember, setSelectedMember] = useState<RosterMember | null>(null);
  const [urgentBookingDetails, setUrgentBookingDetails] = useState<UrgentBookingRequestDetails | null>(null);

  useEffect(() => {
    // If the context's token is missing, try to get it directly from storage.
    // This can happen if the component renders before the Auth context has fully updated after a login redirect.
    if (!token) {
      const storedToken = localStorage.getItem('kk_token');
      if (storedToken) {
        setToken(storedToken);
      }
    }
  }, [token, setToken]);

  const rosterMembers = knowledgeGraph.getRosterMembers();

  const filteredRoster = rosterMembers.filter(
    (m) =>
      m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleBookNow = (member: RosterMember) => {
    if (!isSubscribed) {
      setShowSubscriptionPayment(true);
    } else {
      setSelectedMember(member);
      setUrgentBookingDetails(null); // Reset previous booking details
      setShowUrgentBookingPayment(false); // Ensure payment modal is hidden
      setShowUrgentBookingForm(true); // Show the booking form
    }
  };

  const handleUrgentBookingSubmit = async (details: UrgentBookingRequestDetails) => {
    if (!currentUserId || !selectedMember) {
      notify('Please login and select a member to submit urgent bookings.', 'error');
      return;
    }
    try {
      const eventDate = new Date();
      eventDate.setDate(eventDate.getDate() + Number(details.daysUntilEvent || 0));

      const contractContent = `// URGENT BOOKING CONTRACT DRAFT
// Event: ${details.eventName}
// Artist: ${selectedMember.name}
// Budget: ${details.totalRate} ${details.currency}
// Payment Type: ${details.paymentType}`;
      const response = await fetch(`${API_BASE_URL}/api/proposals/collab`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          eventName: details.eventName,
          artistId: selectedMember.id,
          artistName: selectedMember.name,
          budget: String(details.totalRate),
          currency: details.currency,
          paymentType: details.paymentType,
          milestones: details.paymentType === 'Milestone Based' ? details.milestones : [],
          contractContent,
          isUrgent: true,
          receiverNote: details.message || '',
          proposerId: currentUserId,
          eventDate: eventDate.toISOString(),
          venue: selectedMember?.location || 'TBD',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to submit urgent booking');
      }

      setUrgentBookingDetails(details);
      setShowUrgentBookingForm(false);
      setShowUrgentBookingPayment(true);
      notify(`Urgent proposal submitted. Please proceed with payment.`, 'success');
    } catch (error) {
      console.error('Error submitting urgent booking:', error);
      notify('Failed to send urgent booking request.', 'error');
    }
  };

  const handleSubscriptionSuccess = async () => {
    await activateSubscription();
    setShowSubscriptionPayment(false);
    notify('Roster Access Unlocked! You can now view contacts and ratings.', 'success');
  };

  const handleUrgentBookingPaymentSuccess = (method: 'crypto' | 'fiat') => {
    setShowUrgentBookingPayment(false);
    notify(`Payment for urgent booking successful via ${method}!`, 'success');
    // Optionally, you can perform other actions here, like updating the booking status.
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Briefcase className="text-kala-secondary" /> Community Roster
          </h2>
          <p className="text-kala-400 text-sm">
            Discover talent, venues, and service providers with transparent
            asset inventories.
          </p>
        </div>
        <div className="flex items-center space-x-2">
            <Button variant="outline" onClick={() => onNavigate('/roster_analytics')}>
                <BarChart2 className="w-4 h-4 mr-2"/>
                View Analytics
            </Button>
            {!isSubscribed && (
              <div className="bg-gradient-to-r from-amber-500/20 to-yellow-600/20 border border-yellow-500/30 rounded-lg p-3 flex items-center gap-3">
                <div className="p-2 bg-yellow-500/20 rounded-full text-yellow-400">
                  <Lock className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-sm font-bold text-yellow-200">
                    Guest Access
                  </div>
                  <div className="text-xs text-yellow-200/70">
                    Subscribe to view ratings & contact info
                  </div>
                </div>
                <button
                  onClick={() => setShowSubscriptionPayment(true)}
                  className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-400 text-kala-900 text-xs font-bold rounded transition-colors ml-2"
                >
                  Subscribe Now
                </button>
              </div>
            )}
            {isSubscribed && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-2 flex items-center gap-2 text-green-400 text-sm font-bold">
                <Unlock className="w-4 h-4" /> Premium Access Unlocked
              </div>
            )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-3 w-4 h-4 text-kala-500" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search roster by name or role (e.g., 'Producer', 'Venue', 'Aria Blaze')"
          className="w-full bg-kala-800/50 border border-kala-700 rounded-xl pl-10 pr-4 py-3 text-white focus:ring-2 focus:ring-kala-secondary focus:border-transparent outline-none"
        />
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {filteredRoster.map((member) => {
          const isProtected = member.protected;
          const rating = typeof member.rating === 'number' ? member.rating : 0;

          return (
            <div
              key={member.id}
              className={`relative bg-kala-800/50 border rounded-xl overflow-hidden group transition-all ${isProtected ? 'border-red-900/50' : 'border-kala-700 hover:border-kala-500'}`}
            >
              {isProtected && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-kala-900/20 pointer-events-none">
                  <div className="transform -rotate-12 border-4 border-red-500/50 text-red-500/50 px-6 py-3 font-black text-2xl uppercase tracking-widest rounded-xl shadow-2xl backdrop-blur-sm select-none">
                    Protected Info
                  </div>
                </div>
              )}

              <div
                className={
                  isProtected
                    ? 'filter blur-[3px] grayscale opacity-60 pointer-events-none select-none'
                    : ''
                }
              >
                {/* Card Header */}
                <div className="p-6 flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <img
                        src={member.avatar}
                        alt={member.name}
                        className="w-16 h-16 rounded-xl object-cover border-2 border-kala-700"
                      />
                      {member.verified && (
                        <div className="absolute -bottom-2 -right-2 bg-kala-900 p-1 rounded-full">
                          <ShieldCheck className="w-4 h-4 text-blue-400 fill-blue-400/20" />
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        {member.name}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-kala-400 mt-1">
                        <span className="px-2 py-0.5 bg-kala-700 rounded text-xs text-kala-300 border border-kala-600">
                          {member.role}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {member.location}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    {isSubscribed && !isProtected ? (
                      <div className="flex flex-col items-end">
                        <div className="flex items-center gap-1 text-yellow-400 font-bold text-lg">
                          {rating.toFixed(1)}{' '}
                          <Star className="w-4 h-4 fill-current" />
                        </div>
                        <div className="text-xs text-kala-500">
                          Community Rating
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-end opacity-50 blur-[2px]">
                        <div className="flex items-center gap-1 text-slate-400 font-bold text-lg">
                          5.0 <Star className="w-4 h-4" />
                        </div>
                        <div className="text-xs text-kala-500">
                          Community Rating
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Assets Inventory */}
                <div className="px-6 pb-4">
                  <h4 className="text-xs font-bold text-kala-500 uppercase mb-3">
                    Verifiable Assets from Knowledge Graph
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {(member.assets.ips?.length ?? 0) > 0 && (
                      <Badge
                        icon={Layers}
                        label={`${member.assets.ips?.length ?? 0} IPs`}
                        color="purple"
                      />
                    )}
                    {(member.assets.events?.length ?? 0) > 0 && (
                      <Badge
                        icon={Calendar}
                        label={`${member.assets.events?.length ?? 0} Events`}
                        color="pink"
                      />
                    )}
                    {(member.assets.services?.length ?? 0) > 0 && (
                      <Badge
                        icon={Briefcase}
                        label={`${member.assets.services?.length ?? 0} Services`}
                        color="blue"
                      />
                    )}
                    {(member.assets.productions?.length ?? 0) > 0 && (
                      <Badge
                        icon={Headphones}
                        label={`${member.assets.productions?.length ?? 0} Productions`}
                        color="orange"
                      />
                    )}
                    {(member.assets.nfts?.length ?? 0) > 0 && (
                      <Badge
                        icon={Ticket}
                        label={`${member.assets.nfts?.length ?? 0} NFTs`}
                        color="green"
                      />
                    )}
                    {(member.assets.ips?.length ?? 0) === 0 &&
                      (member.assets.events?.length ?? 0) === 0 &&
                      (member.assets.services?.length ?? 0) === 0 &&
                      (member.assets.productions?.length ?? 0) === 0 && (
                        <span className="text-xs text-kala-600 italic">
                          No public assets linked in the Knowledge Graph.
                        </span>
                      )}
                  </div>
                </div>

                {/* Action Footer */}
                <div className="p-4 bg-kala-900/30 border-t border-kala-700 flex justify-between items-center">
                  <div
                    className={`relative ${!isSubscribed || isProtected ? 'blur-sm select-none' : ''}`}
                  >
                    {isSubscribed && !isProtected ? (
                      <div className="text-xs text-kala-300">
                        {member.subscriberOnly.email}
                      </div>
                    ) : (
                      <div className="text-xs text-kala-400">
                        contact@hidden.com
                      </div>
                    )}
                    {!isSubscribed && !isProtected && (
                      <div className="absolute inset-0 flex items-center">
                        <Lock className="w-4 h-4 text-kala-400 opacity-80" />
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => onViewProfile(member.id)}
                      disabled={isProtected}
                      className="px-4 py-2 rounded-lg text-sm font-bold text-kala-300 hover:text-white hover:bg-kala-800 transition-colors"
                    >
                      View Profile
                    </button>
                    <button
                      onClick={() => handleBookNow(member)}
                      disabled={isProtected && !isSubscribed}
                      className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-2 ${
                        isSubscribed && !isProtected
                          ? 'bg-kala-secondary text-kala-900 hover:bg-cyan-400'
                          : isProtected
                            ? 'bg-kala-800 text-kala-500 cursor-not-allowed'
                            : 'bg-kala-secondary text-kala-900 hover:bg-cyan-400'
                      }`}
                    >
                      {isProtected ? 'Locked' : 'Book Now'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showSubscriptionPayment && (
        <PaymentGateway
          amount={19.99}
          currency="USD / mo"
          itemDescription="Monthly Roster Access"
          onSuccess={handleSubscriptionSuccess}
          onCancel={() => setShowSubscriptionPayment(false)}
          userId={currentUserId}
          token={token}
        />
      )}

      {showUrgentBookingForm && selectedMember && (
        <UrgentBookingForm
          artistName={selectedMember.name}
          onClose={() => setShowUrgentBookingForm(false)}
          onSubmit={handleUrgentBookingSubmit}
        />
      )}

      {showUrgentBookingPayment && urgentBookingDetails && selectedMember && (
        <PaymentGateway
          amount={urgentBookingDetails.totalRate}
          currency={urgentBookingDetails.currency}
          itemDescription={`Urgent booking for ${selectedMember.name}`}
          onSuccess={handleUrgentBookingPaymentSuccess}
          onCancel={() => setShowUrgentBookingPayment(false)}
          userId={currentUserId}
          token={token}
        />
      )}
    </div>
  );
};

const Badge = ({
  icon: Icon,
  label,
  color,
}: {
  icon: React.ElementType;
  label: string;
  color: string;
}) => {
  const colors: Record<string, string> = {
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    pink: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    orange: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    green: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  };

  return (
    <span
      className={`flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-medium border ${colors[color] || colors.blue}`}
    >
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
};

export default Roster;
